/**
 * Project survey config: at most one survey per type.
 */

import {
    normalizeOneQuestion,
    createDefaultQuestion,
    isQuestionAnswerComplete,
    buildStoredAnswerPayload,
    normalizeStoredAnswer,
    ensureMultiOtherIdForLegacyFreeText,
    getFreeTextOptionLabelForDisplay,
    getAnswerCategoryIds,
} from './questionModel';

export const SURVEY_TYPES = Object.freeze({
    PRE_TEST: 'pre_test',
    POST_TEST: 'post_test',
    GENERAL: 'general',
});

export const SURVEY_TYPE_ORDER = Object.freeze([
    SURVEY_TYPES.PRE_TEST,
    SURVEY_TYPES.GENERAL,
    SURVEY_TYPES.POST_TEST,
]);

export const SURVEY_TYPE_LABELS = Object.freeze({
    [SURVEY_TYPES.PRE_TEST]: 'Pre-test',
    [SURVEY_TYPES.POST_TEST]: 'Post-test',
    [SURVEY_TYPES.GENERAL]: 'General survey',
});

export function getSurveyTypeLabel(type) {
    return SURVEY_TYPE_LABELS[type] || String(type || 'Survey');
}

export function createEmptySurvey(type) {
    return {
        type,
        title: '',
        instructions: '',
        questions: [createDefaultQuestion([])],
    };
}

/**
 * @param {unknown} raw
 * @returns {object[]}
 */
export function normalizeSurveysConfig(raw) {
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    const out = [];
    raw.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const type = String(entry.type || '').trim();
        if (!SURVEY_TYPE_ORDER.includes(type) || seen.has(type)) return;
        seen.add(type);
        const title = entry.title == null ? '' : String(entry.title);
        const instructions = entry.instructions == null ? '' : String(entry.instructions);
        const questionsRaw = Array.isArray(entry.questions) ? entry.questions : [];
        const questions = (questionsRaw.length > 0 ? questionsRaw : [createDefaultQuestion([])])
            .map((q, i) => normalizeOneQuestion(q, i));
        out.push({ type, title, instructions, questions });
    });
    return SURVEY_TYPE_ORDER
        .filter((t) => seen.has(t))
        .map((t) => out.find((s) => s.type === t));
}

export function getSurveyByType(surveys, type) {
    const list = normalizeSurveysConfig(surveys);
    return list.find((s) => s.type === type) || null;
}

export function getAvailableSurveyTypesToAdd(surveys) {
    const used = new Set(normalizeSurveysConfig(surveys).map((s) => s.type));
    return SURVEY_TYPE_ORDER.filter((t) => !used.has(t));
}

export function getSurveyDisplayTitle(survey) {
    const title = String(survey?.title ?? '').trim();
    if (title) return title;
    return getSurveyTypeLabel(survey?.type);
}

/**
 * @param {object[]} surveys - normalized
 * @returns {string[]}
 */
export function getSurveyPublishBlockingReasons(surveys) {
    const list = normalizeSurveysConfig(surveys);
    const reasons = [];
    list.forEach((s) => {
        const label = getSurveyDisplayTitle(s);
        if (!Array.isArray(s.questions) || s.questions.length < 1) {
            reasons.push(`${label}: add at least one question.`);
            return;
        }
        s.questions.forEach((q, i) => {
            const qName = String(q.label || '').trim() || `Question ${i + 1}`;
            const allowDisc = Boolean(q.allowDiscreteCategories);
            const allowFt = Boolean(q.allowFreeText);
            if (!allowDisc && !allowFt) {
                reasons.push(`${label} / ${qName}: choose at least one answer type.`);
            }
            if (allowDisc && (!Array.isArray(q.categories) || q.categories.length === 0)) {
                reasons.push(`${label} / ${qName}: add at least one category.`);
            }
        });
    });
    return reasons;
}

/**
 * Whether every question has a complete answer.
 * @param {object} survey
 * @param {Record<string, object>} answers
 */
export function isSurveyResponseComplete(survey, answers) {
    const questions = survey?.questions ?? [];
    if (questions.length === 0) return false;
    return questions.every((q) => isQuestionAnswerComplete(q, answers?.[q.id]));
}

/**
 * Build persisted answers map for one survey.
 * @param {object} survey
 * @param {Record<string, object>} uiAnswers
 */
export function buildSurveyAnswersPayload(survey, uiAnswers) {
    const out = {};
    (survey?.questions || []).forEach((q) => {
        const a = uiAnswers?.[q.id] ?? {};
        const ftLabel = getFreeTextOptionLabelForDisplay(q.freeText?.optionLabel);
        out[q.id] = buildStoredAnswerPayload({
            allowDiscreteCategories: q.allowDiscreteCategories,
            allowFreeText: q.allowFreeText,
            allowMultiple: Boolean(q.allowMultiple),
            categoryId: a.categoryId ?? '',
            categoryLabel: a.categoryLabel ?? '',
            categoryIds: a.categoryIds ?? getAnswerCategoryIds(a),
            categoryLabels: a.categoryLabels,
            freeTextAnswer: a.freeText ?? '',
            freeTextOptionLabel: ftLabel,
        });
    });
    return out;
}

/**
 * Load UI answers from stored response.
 * @param {object} survey
 * @param {object|undefined} storedAnswers
 */
export function migrateSurveyUiAnswers(survey, storedAnswers) {
    const out = {};
    (survey?.questions || []).forEach((q) => {
        const raw = storedAnswers && typeof storedAnswers === 'object'
            ? storedAnswers[q.id]
            : null;
        let normalized = normalizeStoredAnswer(raw);
        normalized = ensureMultiOtherIdForLegacyFreeText(q, normalized);
        const ids = getAnswerCategoryIds(normalized);
        out[q.id] = {
            categoryId: normalized.categoryId ?? ids[0] ?? '',
            categoryLabel: normalized.categoryLabel ?? '',
            categoryIds: ids,
            categoryLabels: Array.isArray(normalized.categoryLabels)
                ? normalized.categoryLabels
                : [],
            freeText: normalized.freeText ?? '',
        };
    });
    return out;
}
